import * as React from 'react';
import styled from 'styled-components';

import useApi from '../../actions/useApi';
import EmojiWhite from '../../assets/emoji.svg';
import { useStateContext } from '../../store/stateContext';
import CommentInput from '../comment-input';
import { SuccessIcon } from '../selected-comment';

import type { User } from '../../types';
interface INewCommentProps {
    title?: string;
    message?: string;
    onFocus?: () => void;
    onCloseCallback?: () => void;
    additionalClasses?: string;
}

const NewCommentContainer = styled.div<{ isDarkMode: boolean; additionalClasses?: string; isUserFormOpen: boolean }>`
    background-color: ${props => props.isDarkMode ? 'white' : '#585568'} !important;
    color: ${props => props.isDarkMode ? 'black' : 'white'} !important;
    border-radius: 16px !important; /* 1rem = 16px */
    display: flex;
    flex-direction: column;
    gap: 16px !important; /* 1rem = 16px */
    ${props => props.isUserFormOpen && 'height: 440px;'}
    width: 100%;
    ${props => props.additionalClasses && props.additionalClasses};
`;

const MessageContainer = styled.div<{ isDarkMode: boolean }>`
    background-color: ${props => props.isDarkMode ? '#EDF0F1' : '#625F74'} !important;
    color: ${props => props.isDarkMode ? 'black' : 'white'} !important;
    border-radius: 16px !important; /* 1rem = 16px */
    padding: 16px !important; /* 1rem = 16px */
    display: flex;
    flex-direction: column;
    gap: 4px !important; /* 0.25rem = 4px */
`;

const TitleText = styled.p<{ isDarkMode: boolean }>`
    margin: 0 !important;
    display: flex;
    flex-direction: row;
    gap: 8px !important; /* 0.5rem = 8px */
    align-items: start;
    font-size: 16px !important;
    font-weight: 700 !important;
    color: ${props => props.isDarkMode ? 'black' : 'white'} !important;
`;

const EmojiImage = styled.img<{ isDarkMode: boolean }>`
    filter: ${props => props.isDarkMode ? 'invert(1)' : 'none'};
`;

const MessageText = styled.p<{ isDarkMode: boolean }>`
    all: revert;
    margin: 0 !important;
    font-size: 14px !important; /* 0.875rem = 14px */
    color: ${props => props.isDarkMode ? 'black' : 'white'} !important;
`;

const SuccessMessage = styled.div`
    height: 40px !important;
    border-radius: 24px !important; /* 1.5rem = 24px */
    background-color: #23C68D !important;
    padding: 0 16px !important; /* 1rem = 16px */
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
`;

const SuccessText = styled.p`
    color: white !important;
`;

const NewComment: React.FunctionComponent<INewCommentProps> = ({
    title = "Leave us your feedback!",
    message = 'Tell us why you like this item',
    onFocus,
    onCloseCallback,
    additionalClasses
}) => {
    const { state } = useStateContext();
    const { addComment } = useApi();
    const [success, setSuccess] = React.useState(false);
    const isUserFormOpen = state.userForm.isOpen;
    const handleSubmit = async (message: string, values: Partial<User> | undefined) => {
        await addComment(`${state.productInfo?.id}`, message, values);
        setSuccess(true);
        setTimeout(() => {
            setSuccess(false);
            onCloseCallback && onCloseCallback()
        }, 3000);
    }

    const isDarkMode = state.themeMode === 'dark';

    return (
        <NewCommentContainer isDarkMode={isDarkMode} additionalClasses={additionalClasses} isUserFormOpen={isUserFormOpen}>
            <MessageContainer isDarkMode={isDarkMode}>
                <TitleText isDarkMode={isDarkMode}>
                    <EmojiImage src={EmojiWhite} isDarkMode={isDarkMode} />
                    {title}
                </TitleText>
                <MessageText isDarkMode={isDarkMode}>{message}</MessageText>
            </MessageContainer>
            {
                success ? (
                    <SuccessMessage>
                        <SuccessText>Comment posted!</SuccessText>
                        <SuccessIcon />
                    </SuccessMessage>
                ) : (
                    <CommentInput placeholder='Type here' onSubmit={handleSubmit} onFocus={onFocus} label='Post my message' />
                )
            }
        </NewCommentContainer>
    );
};

export default NewComment;
